// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img from "@keller/MobiusOverrides/Img";
import { ImgProps } from "@keller/MobiusOverrides/Img";
import Tooltip, { TooltipPresentationProps } from "@keller/MobiusOverrides/Tooltip";
import React, { FC } from "react";
import { css } from "styled-components";

export type ProductBadgeType = { altText?: string; src?: string; tooltipText?: string };

interface OwnProps extends WidgetProps {}

type Props = OwnProps & HasProduct;

interface ProductBadgesStyles {
    wrapper?: InjectableCss;
    badgeImg?: ImgProps;
    tooltip?: TooltipPresentationProps;
}

const productBadgesStyles: ProductBadgesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            margin-top: 20px;
            align-items: flex-start;
        `,
    },
    badgeImg: {
        css: css`
            &&& {
                max-width: 40px;
                margin: 0;
            }
        `,
    },
    tooltip: {
        cssOverrides: {
            tooltipClickable: css`
                height: 40px;
                width: 40px;
            `,
        },
    },
};

const styles = productBadgesStyles;

const ProductBadges: FC<Props> = ({ product }) => {
    const badges: ProductBadgeType[] | undefined = product.properties.badges
        ? JSON.parse(product.properties.badges)
        : undefined;

    if (!badges || badges.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_productBadges">
            {badges.map((badge, index) => (
                <>
                    <Tooltip
                        text={badge.tooltipText ? badge.tooltipText : badge.altText}
                        {...styles.tooltip}
                        triggerComponent={
                            <Img
                                key={badge.src}
                                width={40}
                                height={40}
                                src={badge.src}
                                altText={badge.altText || translate(`Badges_${index}`)}
                                {...styles.badgeImg}
                            />
                        }
                    />
                </>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductBadges),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
